/**
 * Max One In Cart - Shopify Plugin
 * Limits specific products to one purchase per session
 * Supports exclusive products that must be purchased alone
 *
 * @version 2.4.0 - Fetch fresh cart state before exclusive validation
 */

(function () {
  'use strict';

  // Exit if not configured
  if (!window.MaxOneInCart || !window.MaxOneInCart.config || !window.MaxOneInCart.config.enabled) {
    return;
  }

  const config = window.MaxOneInCart.config;
  const settings = config.settings;
  const STORAGE_KEY = 'maxOneInCart_addedProducts';

  /**
   * Session Storage Manager
   */
  const StorageManager = {
    getAddedProducts: function () {
      try {
        const data = sessionStorage.getItem(STORAGE_KEY);
        return data ? JSON.parse(data) : {};
      } catch (e) {
        console.warn('MaxOneInCart: Unable to read session storage', e);
        return {};
      }
    },

    setAddedProducts: function (products) {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(products));
      } catch (e) {
        console.warn('MaxOneInCart: Unable to write to session storage', e);
      }
    },

    markProductAdded: function (handle) {
      const products = this.getAddedProducts();
      products[handle.toLowerCase()] = {
        addedAt: Date.now(),
        handle: handle.toLowerCase()
      };
      this.setAddedProducts(products);
    },

    isProductAdded: function (handle) {
      const products = this.getAddedProducts();
      return !!products[handle.toLowerCase()];
    },

    removeProduct: function (handle) {
      const products = this.getAddedProducts();
      delete products[handle.toLowerCase()];
      this.setAddedProducts(products);
    },

    syncWithCart: function (cartItems) {
      const products = this.getAddedProducts();
      const cartHandles = Object.keys(cartItems || {}).map(h => h.toLowerCase());

      config.restrictedProducts.forEach(handle => {
        const normalizedHandle = handle.toLowerCase();
        if (cartHandles.includes(normalizedHandle) && !products[normalizedHandle]) {
          products[normalizedHandle] = {
            addedAt: Date.now(),
            handle: normalizedHandle,
            syncedFromCart: true
          };
        }
      });

      this.setAddedProducts(products);
    }
  };

  /**
   * Validation Logic
   */
  const Validator = {
    isRestrictedProduct: function (handle) {
      if (!handle) return false;
      const normalizedHandle = handle.toLowerCase();
      return config.restrictedProducts.some(h => h.toLowerCase() === normalizedHandle);
    },

    isExclusiveProduct: function (handle) {
      if (!handle) return false;
      const normalizedHandle = handle.toLowerCase();
      const exclusiveProducts = config.exclusiveProducts || [];
      return exclusiveProducts.some(h => h.toLowerCase() === normalizedHandle);
    },

    isExclusiveProductId: function (productId) {
      if (!productId) return false;
      const idStr = String(productId);
      const exclusiveIds = config.exclusiveProductIds || [];
      return exclusiveIds.some(id => String(id) === idStr);
    },

    hasExclusiveInCart: function () {
      const cartItems = config.cartItems || {};
      const exclusiveProducts = config.exclusiveProducts || [];
      for (const cartHandle in cartItems) {
        if (exclusiveProducts.some(h => h.toLowerCase() === cartHandle.toLowerCase())) {
          return true;
        }
      }
      return false;
    },

    hasNonExclusiveInCart: function () {
      const cartItems = config.cartItems || {};
      const exclusiveProducts = config.exclusiveProducts || [];
      for (const cartHandle in cartItems) {
        const isExclusive = exclusiveProducts.some(h => h.toLowerCase() === cartHandle.toLowerCase());
        if (!isExclusive) {
          return true;
        }
      }
      return false;
    },

    cartHasItems: function () {
      return Object.keys(config.cartItems || {}).length > 0;
    },

    canAddProduct: function (handle, productId) {
      // First check exclusive product rules (by handle OR product ID)
      const exclusiveCheck = this.canAddExclusiveProduct(handle, productId);
      if (!exclusiveCheck.allowed) {
        return exclusiveCheck;
      }

      // Then check restricted product rules (one per session)
      if (!this.isRestrictedProduct(handle)) {
        return { allowed: true };
      }

      if (StorageManager.isProductAdded(handle)) {
        return {
          allowed: false,
          reason: 'session',
          message: settings.errorMessage
        };
      }

      const cartItems = config.cartItems || {};
      const normalizedHandle = handle.toLowerCase();
      for (const cartHandle in cartItems) {
        if (cartHandle.toLowerCase() === normalizedHandle) {
          return {
            allowed: false,
            reason: 'cart',
            message: settings.errorMessage
          };
        }
      }

      return { allowed: true };
    },

    canAddExclusiveProduct: function (handle, productId) {
      const exclusiveInCart = this.hasExclusiveInCart();
      const cartHasItems = this.cartHasItems();

      // Check if this product is exclusive (by handle OR by ID)
      const isExclusive = (handle && this.isExclusiveProduct(handle)) ||
                          (productId && this.isExclusiveProductId(productId));

      // Case 1: Trying to add an exclusive product when cart has other (non-exclusive) items
      if (isExclusive && cartHasItems && !exclusiveInCart) {
        return {
          allowed: false,
          reason: 'cart_not_empty',
          message: settings.exclusiveErrorCartNotEmpty
        };
      }

      // Case 2: Trying to add any product when an exclusive product is already in cart
      if (!isExclusive && exclusiveInCart) {
        return {
          allowed: false,
          reason: 'exclusive_in_cart',
          message: settings.exclusiveErrorHasExclusive
        };
      }

      return { allowed: true };
    },

    canCheckout: function () {
      if (!settings.blockCheckout) {
        return { allowed: true };
      }

      const cartItems = config.cartItems || {};
      const violations = [];

      for (const handle in cartItems) {
        if (this.isRestrictedProduct(handle) && cartItems[handle].quantity > 1) {
          violations.push({
            handle: handle,
            quantity: cartItems[handle].quantity
          });
        }
      }

      if (violations.length > 0) {
        return {
          allowed: false,
          violations: violations,
          message: settings.checkoutMessage
        };
      }

      return { allowed: true };
    }
  };

  /**
   * UI Manager
   */
  const UI = {
    showError: function (message, targetElement) {
      this.clearErrors();

      const errorDiv = document.createElement('div');
      errorDiv.className = 'max-one-error-message max-one-error-' + settings.notificationStyle;
      errorDiv.setAttribute('role', 'alert');
      errorDiv.setAttribute('aria-live', 'polite');
      errorDiv.style.cssText = `
        --max-one-error-color: ${settings.errorColor};
        --max-one-error-bg: ${settings.errorBgColor};
        position: fixed;
        top: 20px;
        left: 50%;
        transform: translateX(-50%);
        z-index: 999999;
        padding: 15px 25px;
        background: ${settings.errorBgColor};
        color: ${settings.errorColor};
        border-radius: var(--sp-2d5);
        font-weight: 600;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        width: min(500px, 95vw);
        text-align: left;
      `;

      errorDiv.innerHTML = `
        <div style="display: flex; align-items: center; gap: 10px;">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <span>${this.escapeHtml(message)}</span>
          <button onclick="this.parentElement.parentElement.remove()" style="background:none;border:none;font-size:20px;cursor:pointer;color:inherit;">&times;</button>
        </div>
      `;

      document.body.appendChild(errorDiv);

      setTimeout(() => {
        if (errorDiv.parentNode) errorDiv.remove();
      }, 5000);
    },

    clearErrors: function () {
      document.querySelectorAll('.max-one-error-message').forEach(el => el.remove());
    },

    escapeHtml: function (text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    disableAddToCart: function (button, message) {
      if (!button) return;
      button.disabled = true;
      button.classList.add('max-one-disabled');
      if (!button.getAttribute('data-original-text')) {
        button.setAttribute('data-original-text', button.textContent);
      }
      button.textContent = message || 'Already in cart';
    },

    findAddToCartButton: function () {
      const selectors = [
        'button[type="submit"][name="add"]',
        'button.product-form__submit',
        'button.add-to-cart',
        'button[data-add-to-cart]',
        '.product-form button[type="submit"]',
        'form[action*="/cart/add"] button[type="submit"]',
        '#AddToCart',
        '[data-action="add-to-cart"]'
      ];

      for (const selector of selectors) {
        const button = document.querySelector(selector);
        if (button) return button;
      }
      return null;
    }
  };

  /**
   * Cart Drawer/Side Cart Handler
   * v2.1 - Event-based only, no polling or MutationObserver
   */
  const DrawerCartHandler = {
    init: function () {
      // Only use event-based interception (no polling, no MutationObserver)
      this.interceptQuantityButtons();
    },

    interceptQuantityButtons: function () {
      // Intercept clicks on quantity-related buttons
      document.addEventListener('click', (e) => {
        const target = e.target;
        const button = target.closest('button, [role="button"], .btn, a');

        if (!button) return;

        const buttonText = (button.textContent || '').toLowerCase().trim();
        const buttonClass = (button.className || '').toLowerCase();
        const ariaLabel = (button.getAttribute('aria-label') || '').toLowerCase();

        // Check if this is a quantity increase button
        const isIncreaseButton =
          buttonText === '+' ||
          buttonClass.includes('plus') ||
          buttonClass.includes('increase') ||
          buttonClass.includes('increment') ||
          ariaLabel.includes('increase') ||
          ariaLabel.includes('add more') ||
          button.dataset.action === 'increase' ||
          button.dataset.action === 'increment';

        if (isIncreaseButton) {
          // Find the associated product
          const cartItem = button.closest('[data-cart-item], [data-line-item], .cart-item, .cart__item, .drawer__item, [class*="cart"][class*="item"]');
          if (cartItem) {
            const handle = this.getHandleFromCartItem(cartItem);
            if (handle && Validator.isRestrictedProduct(handle)) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              UI.showError(settings.errorMessage);
              return false;
            }
          }
        }
      }, true);

      // Intercept input changes for quantity fields
      document.addEventListener('change', (e) => {
        const input = e.target;
        if (input.tagName !== 'INPUT') return;
        if (input.type !== 'number' && !input.name?.includes('quantity') && !input.name?.includes('qty')) return;

        const cartItem = input.closest('[data-cart-item], [data-line-item], .cart-item, .cart__item, .drawer__item, [class*="cart"][class*="item"]');
        if (cartItem) {
          const handle = this.getHandleFromCartItem(cartItem);
          if (handle && Validator.isRestrictedProduct(handle)) {
            const newQty = parseInt(input.value, 10);
            if (newQty > 1) {
              e.preventDefault();
              e.stopPropagation();
              input.value = 1;
              UI.showError(settings.errorMessage);
              // Force the cart to correct via API (single call)
              this.forceQuantityToOne(cartItem);
            }
          }
        }
      }, true);

      // Intercept beforeinput for quantity fields
      document.addEventListener('beforeinput', (e) => {
        const input = e.target;
        if (input.tagName !== 'INPUT') return;

        const cartItem = input.closest('[data-cart-item], [data-line-item], .cart-item, .cart__item, .drawer__item, [class*="cart"][class*="item"]');
        if (cartItem) {
          const handle = this.getHandleFromCartItem(cartItem);
          if (handle && Validator.isRestrictedProduct(handle)) {
            const currentValue = parseInt(input.value, 10) || 1;
            // If trying to increase, block it
            if (e.data && parseInt(e.data, 10) > currentValue) {
              e.preventDefault();
            }
          }
        }
      }, true);
    },

    getHandleFromCartItem: function (cartItem) {
      // Try data attribute
      if (cartItem.dataset.productHandle) return cartItem.dataset.productHandle;
      if (cartItem.dataset.handle) return cartItem.dataset.handle;

      // Try link
      const link = cartItem.querySelector('a[href*="/products/"]');
      if (link) {
        const match = link.href.match(/\/products\/([^/?#]+)/);
        if (match) return match[1];
      }

      // Try image
      const img = cartItem.querySelector('img[src*="/products/"]');
      if (img) {
        const match = img.src.match(/\/products\/([^/?#]+)/);
        if (match) return match[1];
      }

      // Try from our cart state
      const qtyInput = cartItem.querySelector('input[name*="quantity"], input[type="number"]');
      if (qtyInput) {
        const lineKey = cartItem.dataset.lineKey || cartItem.dataset.key || qtyInput.dataset.key;
        if (lineKey && config.cartItems) {
          for (const handle in config.cartItems) {
            if (config.cartItems[handle].key === lineKey) {
              return handle;
            }
          }
        }
      }

      return null;
    },

    forceQuantityToOne: function (cartItem) {
      // Get line item key
      const keyInput = cartItem.querySelector('[name*="id"], [name*="key"], [data-line-key]');
      const lineKey = keyInput?.value || cartItem.dataset.lineKey || cartItem.dataset.key;

      if (!lineKey) return;

      // Force the cart to update via Shopify API (single call, no follow-up)
      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity: 1
        })
      }).then(response => response.json())
        .then(cart => {
          // Update local state from response (no additional fetch needed)
          if (cart && cart.items) {
            config.cartItems = {};
            cart.items.forEach(item => {
              config.cartItems[item.handle] = {
                quantity: item.quantity,
                variantId: item.variant_id,
                productId: item.product_id,
                key: item.key
              };
            });
          }
        })
        .catch(console.error);
    }
  };

  /**
   * Cart API Interceptor
   * v2.1 - No automatic updateCartState calls
   */
  const CartInterceptor = {
    init: function () {
      this.interceptFetch();
      this.setupClickCapture();
    },

    // Capture product handle from DOM when add-to-cart is clicked
    setupClickCapture: function () {
      const self = this;
      document.addEventListener('click', function (e) {
        const button = e.target.closest('button, input[type="submit"], [data-add-to-cart], .add-to-cart, [class*="add"][class*="cart"]');
        if (!button) return;

        // Find the product container
        const productCard = button.closest('[data-product-handle], [data-handle], .product-card, .product-item, .card-product, .product, article, .grid-item, form[action*="/cart/add"]');
        if (!productCard) return;

        let handle = null;

        // Try data attributes
        handle = productCard.dataset.productHandle || productCard.dataset.handle;

        // Try finding a product link
        if (!handle) {
          const link = productCard.querySelector('a[href*="/products/"]');
          if (link) {
            const match = link.href.match(/\/products\/([^/?#]+)/);
            if (match) handle = match[1];
          }
        }

        // Try form action
        if (!handle) {
          const form = button.closest('form[action*="/products/"]') || productCard.querySelector('form[action*="/products/"]');
          if (form) {
            const match = form.action.match(/\/products\/([^/?#]+)/);
            if (match) handle = match[1];
          }
        }

        // Try hidden input
        if (!handle) {
          const handleInput = productCard.querySelector('input[name="handle"], input[data-handle]');
          if (handleInput) handle = handleInput.value || handleInput.dataset.handle;
        }

        if (handle) {
          self.lastClickedProductHandle = handle.toLowerCase();
          console.log('MaxOneInCart: Captured handle from click', handle);
        }
      }, true);
    },

    interceptFetch: function () {
      const self = this;
      const originalFetch = window.fetch;

      window.fetch = function (url, options) {
        const urlString = typeof url === 'string' ? url : (url?.url || url?.toString() || '');

        // Block add requests for restricted/exclusive products
        if (self.isCartAddRequest(urlString, options)) {
          const parsedBody = self.parseBody(options?.body);
          const extractedHandle = self.extractProductHandle(options);
          const productHandle = extractedHandle || config.currentProductHandle;
          // Extract product ID from form data (product-id field)
          const productId = parsedBody?.['product-id'] || parsedBody?.product_id || config.currentProductId;

          // Fetch fresh cart state before validating exclusive products
          return originalFetch('/cart.js', { method: 'GET' })
            .then(res => res.json())
            .then(cart => {
              // Update local cart state
              if (cart && cart.items) {
                config.cartItems = {};
                cart.items.forEach(item => {
                  config.cartItems[item.handle] = {
                    quantity: item.quantity,
                    variantId: item.variant_id,
                    productId: item.product_id,
                    key: item.key
                  };
                });
              }

              console.log('MaxOneInCart: Add request intercepted', {
                url: urlString,
                bodyType: options?.body ? options.body.constructor.name : 'none',
                parsedBody,
                extractedHandle,
                productId,
                currentProductHandle: config.currentProductHandle,
                finalHandle: productHandle,
                cartItems: config.cartItems,
                exclusiveInCart: Validator.hasExclusiveInCart(),
                isExclusiveByHandle: productHandle ? Validator.isExclusiveProduct(productHandle) : false,
                isExclusiveById: productId ? Validator.isExclusiveProductId(productId) : false
              });

              // Validate using both handle and product ID
              const validation = Validator.canAddProduct(productHandle, productId);
              console.log('MaxOneInCart: Validation result', validation);
              if (!validation.allowed) {
                UI.showError(validation.message);
                return new Response(JSON.stringify({ error: validation.message }), { status: 422 });
              }

              // Proceed with original add request
              return originalFetch.apply(this, [url, options]);
            })
            .catch(() => {
              // If cart fetch fails, proceed anyway
              return originalFetch.apply(this, [url, options]);
            });
        }

        // Block quantity increase requests for restricted products
        if (self.isCartChangeRequest(urlString, options)) {
          const body = self.parseBody(options?.body);
          if (body && body.quantity > 1) {
            const handle = self.getHandleFromLineKey(body.id) || self.getHandleFromVariantId(body.id);
            if (handle && Validator.isRestrictedProduct(handle)) {
              UI.showError(settings.errorMessage);
              // Force quantity to 1
              const newBody = { ...body, quantity: 1 };
              options = { ...options, body: JSON.stringify(newBody) };
            }
          }
        }

        // Block update requests that increase quantity for restricted products
        if (self.isCartUpdateRequest(urlString, options)) {
          const body = self.parseBody(options?.body);
          if (body && body.updates) {
            let modified = false;
            const updates = { ...body.updates };
            for (const key in updates) {
              if (updates[key] > 1) {
                const handle = self.getHandleFromLineKey(key) || self.getHandleFromVariantId(key);
                if (handle && Validator.isRestrictedProduct(handle)) {
                  updates[key] = 1;
                  modified = true;
                  UI.showError(settings.errorMessage);
                }
              }
            }
            if (modified && options && options.body) {
              options = { ...options, body: JSON.stringify({ ...body, updates }) };
            }
          }
        }

        return originalFetch.apply(this, [url, options]).then(response => {
          // After successful add, mark product and update local state from response
          if (self.isCartAddRequest(urlString, options) && response.ok) {
            const productHandle = self.extractProductHandle(options) || config.currentProductHandle;
            if (productHandle && Validator.isRestrictedProduct(productHandle)) {
              StorageManager.markProductAdded(productHandle);

              // Update local cart state from response (clone to read body)
              response.clone().json().then(cart => {
                if (cart && cart.items) {
                  config.cartItems = {};
                  cart.items.forEach(item => {
                    config.cartItems[item.handle] = {
                      quantity: item.quantity,
                      variantId: item.variant_id,
                      productId: item.product_id,
                      key: item.key
                    };
                  });
                }
              }).catch(() => {});

              if (settings.disableAfterAdd) {
                const button = UI.findAddToCartButton();
                UI.disableAddToCart(button);
              }
            }
          }

          // For change/update requests, update local state from response
          if ((self.isCartChangeRequest(urlString, options) || self.isCartUpdateRequest(urlString, options)) && response.ok) {
            response.clone().json().then(cart => {
              if (cart && cart.items) {
                config.cartItems = {};
                cart.items.forEach(item => {
                  config.cartItems[item.handle] = {
                    quantity: item.quantity,
                    variantId: item.variant_id,
                    productId: item.product_id,
                    key: item.key
                  };
                });
              }
            }).catch(() => {});
          }

          return response;
        });
      };
    },

    parseBody: function (body) {
      if (!body) return null;

      // Handle JSON string
      if (typeof body === 'string') {
        try {
          return JSON.parse(body);
        } catch (e) {
          // Try URL-encoded form data (key=value&key2=value2)
          if (body.includes('=')) {
            const params = new URLSearchParams(body);
            const result = {};
            for (const [key, value] of params) {
              result[key] = value;
            }
            return result;
          }
          return null;
        }
      }

      // Handle FormData
      if (body instanceof FormData) {
        const result = {};
        for (const [key, value] of body.entries()) {
          result[key] = value;
        }
        return result;
      }

      // Handle URLSearchParams
      if (body instanceof URLSearchParams) {
        const result = {};
        for (const [key, value] of body) {
          result[key] = value;
        }
        return result;
      }

      return body;
    },

    isCartAddRequest: function (url, options) {
      if (!url) return false;
      return url.includes('/cart/add') && (!options?.method || options.method.toUpperCase() === 'POST');
    },

    isCartChangeRequest: function (url, options) {
      if (!url) return false;
      return url.includes('/cart/change') && (!options?.method || options.method.toUpperCase() === 'POST');
    },

    isCartUpdateRequest: function (url, options) {
      if (!url) return false;
      return url.includes('/cart/update') && (!options?.method || options.method.toUpperCase() === 'POST');
    },

    extractProductHandle: function (options) {
      if (!options?.body) return null;
      const body = this.parseBody(options.body);
      if (!body) return null;

      // Try body.items array (AJAX API format)
      if (body.items && Array.isArray(body.items)) {
        for (const item of body.items) {
          const handle = item.handle || this.getHandleFromVariantId(item.id);
          if (handle && (Validator.isRestrictedProduct(handle) || Validator.isExclusiveProduct(handle))) {
            return handle;
          }
        }
        if (body.items.length > 0) {
          return body.items[0].handle || this.getHandleFromVariantId(body.items[0].id);
        }
      }

      // Try direct handle property
      if (body.handle) return body.handle;

      // Try to get handle from variant ID
      if (body.id) {
        const handleFromVariant = this.getHandleFromVariantId(body.id);
        if (handleFromVariant) return handleFromVariant;
      }

      // Try to find handle from the last clicked product element
      if (this.lastClickedProductHandle) {
        return this.lastClickedProductHandle;
      }

      return null;
    },

    // Store the last clicked product's handle (set by click listener)
    lastClickedProductHandle: null,

    getHandleFromVariantId: function (variantId) {
      if (config.currentProductHandle && config.currentVariantId == variantId) {
        return config.currentProductHandle;
      }

      for (const handle in config.cartItems) {
        if (config.cartItems[handle].variantId == variantId) {
          return handle;
        }
      }

      return config.currentProductHandle || null;
    },

    getHandleFromLineKey: function (lineKey) {
      if (!lineKey || !config.cartItems) return null;

      for (const handle in config.cartItems) {
        if (config.cartItems[handle].key === lineKey) {
          return handle;
        }
      }
      return null;
    }
  };

  /**
   * Form Handler
   */
  const FormHandler = {
    init: function () {
      this.setupFormListeners();
      this.setupInitialState();
    },

    setupFormListeners: function () {
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!form.action || !form.action.includes('/cart/add')) return;

        const handle = this.getFormProductHandle(form);
        if (!handle) return;

        const validation = Validator.canAddProduct(handle);
        if (!validation.allowed) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          UI.showError(validation.message);
          return false;
        }
      }, true);

      document.addEventListener('click', (e) => {
        const button = e.target.closest('button[type="submit"], input[type="submit"], [data-add-to-cart]');
        if (!button) return;

        const form = button.closest('form');
        if (!form || !form.action || !form.action.includes('/cart/add')) return;

        const handle = this.getFormProductHandle(form);
        if (!handle) return;

        const validation = Validator.canAddProduct(handle);
        if (!validation.allowed) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          UI.showError(validation.message);
          return false;
        }
      }, true);
    },

    getFormProductHandle: function (form) {
      const handleInput = form.querySelector('[name="handle"]');
      if (handleInput) return handleInput.value;

      if (form.dataset.productHandle) return form.dataset.productHandle;

      const variantInput = form.querySelector('[name="id"]');
      if (variantInput) {
        return CartInterceptor.getHandleFromVariantId(variantInput.value);
      }

      return config.currentProductHandle;
    },

    setupInitialState: function () {
      // Skip if server-side already disabled the button (prevents duplicate text)
      if (window.MaxOneInCart.serverDisabled) {
        return;
      }

      if (config.currentProductRestricted) {
        const validation = Validator.canAddProduct(config.currentProductHandle);
        if (!validation.allowed && settings.disableAfterAdd) {
          const button = UI.findAddToCartButton();
          UI.disableAddToCart(button, 'Already in cart');
        }
      }

      if (settings.hideQuantity && config.currentProductRestricted) {
        document.querySelectorAll('.quantity, .quantity-selector, input[name="quantity"]').forEach(el => {
          el.style.display = 'none';
        });
        document.querySelectorAll('input[name="quantity"]').forEach(input => {
          input.value = '1';
          input.setAttribute('max', '1');
        });
      }
    }
  };

  /**
   * Checkout Validator
   */
  const CheckoutValidator = {
    init: function () {
      if (!settings.blockCheckout) return;

      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href*="/checkout"], [name="checkout"], button[name="checkout"], .checkout-button, [class*="checkout"]');
        if (!link) return;

        // Check if any restricted product has quantity > 1
        for (const handle in config.cartItems) {
          if (Validator.isRestrictedProduct(handle) && config.cartItems[handle].quantity > 1) {
            e.preventDefault();
            e.stopPropagation();
            UI.showError(settings.checkoutMessage);
            return false;
          }
        }
      }, true);
    }
  };

  /**
   * Initialize Plugin
   */
  function init() {
    StorageManager.syncWithCart(config.cartItems);

    CartInterceptor.init();
    FormHandler.init();
    CheckoutValidator.init();
    DrawerCartHandler.init();

    // Expose API
    window.MaxOneInCart.api = {
      isRestricted: Validator.isRestrictedProduct.bind(Validator),
      isExclusive: Validator.isExclusiveProduct.bind(Validator),
      canAdd: Validator.canAddProduct.bind(Validator),
      canCheckout: Validator.canCheckout.bind(Validator),
      markAdded: StorageManager.markProductAdded.bind(StorageManager),
      isAdded: StorageManager.isProductAdded.bind(StorageManager),
      hasExclusiveInCart: Validator.hasExclusiveInCart.bind(Validator)
    };

    console.log('MaxOneInCart v2.4: Initialized', {
      restrictedProducts: config.restrictedProducts,
      exclusiveProducts: config.exclusiveProducts || [],
      exclusiveProductIds: config.exclusiveProductIds || [],
      currentProduct: config.currentProductHandle
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
